import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { format, addDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaCalendarAlt, FaEye, FaInfoCircle, FaChartBar, FaCheck, FaExpandAlt, FaCompressAlt, FaRegFileAlt } from 'react-icons/fa';
import { createWatchedPick } from '../services/api';
import { useGamePicks } from '../hooks/useApiQueries';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { formatPickTypeLabel, formatPickValue } from '../utils/formatPickType';
import { isGameNotStarted } from '../components/Predictions/TrackPickButton';
import StatusWithGamedayLink from '../components/Predictions/StatusWithGamedayLink';
import PlayerWithAvatar from '../components/PlayerWithAvatar';
import TeamWithLogo from '../components/TeamWithLogo';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';
import { buildModelPageDeepLink, buildTeamPicksPath } from '../utils/modelPathForPickType';

/** Order for sorting by model: Total Bases, Hit, 2+ Hits, Home Run */
const PICK_TYPE_ORDER = { total_bases: 0, hit: 1, two_hit: 2, hr: 3 };

/** Game statuses that mean the game is complete (show "Final" and pick results). */
const FINAL_GAME_STATUSES = ['Final', 'Game Over', 'Completed Early'];

function isGameFinal(gameStatus) {
  if (!gameStatus || typeof gameStatus !== 'string') return false;
  return FINAL_GAME_STATUSES.includes(gameStatus.trim());
}

/**
 * First opposing pitcher from consolidated-backed picks (pitcher the batter faces).
 * Away picks list the home starter; home picks list the away starter.
 *
 * @param {Array<Record<string, unknown>> | undefined} picks
 * @returns {{ name: string, id?: number, hand?: string } | null}
 */
function firstOpposingPitcherFromPicks(picks) {
  if (!picks || !Array.isArray(picks)) return null;
  for (const raw of picks) {
    const p = raw && typeof raw === 'object' ? raw : {};
    const name = String(p.pitcher_name || p.pitcherName || '').trim();
    if (!name) continue;
    const rawId = p.pitcher_id ?? p.pitcherId;
    let id;
    if (rawId != null && rawId !== '') {
      const n = Number(rawId);
      if (Number.isFinite(n)) id = n;
    }
    const handRaw = p.pitcher_hand || p.pitcherHand;
    const hand = handRaw != null && String(handRaw).trim() ? String(handRaw).trim() : undefined;
    return { name, id, hand };
  }
  return null;
}

/**
 * Probable starter from game-level fields enriched by the API / generator.
 *
 * @param {Record<string, unknown> | undefined} game
 * @param {'away' | 'home'} side
 * @returns {{ name: string, id?: number, hand?: string } | null}
 */
function starterFromGameFields(game, side) {
  if (!game || typeof game !== 'object') return null;
  const nameKey = side === 'away' ? 'away_pitcher_name' : 'home_pitcher_name';
  const idKey = side === 'away' ? 'away_pitcher_id' : 'home_pitcher_id';
  const handKey = side === 'away' ? 'away_pitcher_hand' : 'home_pitcher_hand';
  const name = String(game[nameKey] || game[side === 'away' ? 'awayPitcherName' : 'homePitcherName'] || '').trim();
  if (!name) return null;
  const rawId = game[idKey] ?? game[side === 'away' ? 'awayPitcherId' : 'homePitcherId'];
  let id;
  if (rawId != null && rawId !== '') {
    const n = Number(rawId);
    if (Number.isFinite(n)) id = n;
  }
  const handRaw = game[handKey] ?? game[side === 'away' ? 'awayPitcherHand' : 'homePitcherHand'];
  const hand = handRaw != null && String(handRaw).trim() ? String(handRaw).trim() : undefined;
  return { name, id, hand };
}

/**
 * Resolve away/home starters: prefer game-level fields, fall back to favorable picks.
 *
 * @param {Record<string, unknown> | undefined} game
 * @returns {{ away: { name: string, id?: number, hand?: string } | null, home: { name: string, id?: number, hand?: string } | null }}
 */
function resolveGameStarters(game) {
  const away =
    starterFromGameFields(game, 'away') ||
    firstOpposingPitcherFromPicks(game?.favorable_picks?.home);
  const home =
    starterFromGameFields(game, 'home') ||
    firstOpposingPitcherFromPicks(game?.favorable_picks?.away);
  return { away, home };
}

/**
 * Display label for a starter (name + optional throws hand).
 *
 * @param {{ name: string, hand?: string } | null | undefined} p
 * @returns {string | null}
 */
function pitcherStarterLabel(p) {
  if (!p || !p.name) return null;
  return p.hand ? `${p.name} (${p.hand})` : p.name;
}

/**
 * Format pick result for display when we have actualValue/hit (e.g. "2 TB ✅", "0 hits ❌").
 * @param {{ pick_type?: string, actualValue?: number | null, hit?: boolean | null }} pick
 * @param {{ assumeFinal?: boolean }} [opts] When true (game already known final), omit "(in progress)" if hit unset.
 * @returns {string} Empty string if no result, otherwise "N TB ✅" / "N hits ❌" etc.
 */
function formatPickResult(pick, opts = {}) {
  if (pick == null) return '';
  const val = pick.actualValue;
  if (val === undefined || val === null) return '';
  const type = (pick.pick_type || '').toLowerCase().trim();
  const hit = pick.hit;
  const unit = type === 'total_bases' ? 'TB' : type === 'hr' ? 'HR' : 'hits';
  const result = `${Number(val)} ${unit}`;
  if (hit === true) return `${result} ✅`;
  if (hit === false) return `${result} ❌`;
  if (opts.assumeFinal) return result;
  return `${result} (in progress)`;
}

/**
 * Sort picks by model type (Total Bases → Hit → 2+ Hits → HR), then by prediction value descending.
 * @param {Array<{ pick_type?: string, prediction_value?: number }>} picks
 * @returns {Array} New sorted array (does not mutate input).
 */
function sortPicksByModelThenValue(picks) {
  if (!picks || !Array.isArray(picks)) return [];
  return [...picks].sort((a, b) => {
    const orderA = PICK_TYPE_ORDER[(a.pick_type || '').toLowerCase().trim()] ?? 99;
    const orderB = PICK_TYPE_ORDER[(b.pick_type || '').toLowerCase().trim()] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    const valA = a.prediction_value != null ? Number(a.prediction_value) : -Infinity;
    const valB = b.prediction_value != null ? Number(b.prediction_value) : -Infinity;
    return valB - valA;
  });
}

function parseAmericanOdds(str) {
  if (str == null || typeof str !== 'string') return null;
  const s = str.trim().replace(/^\+/, '');
  if (s === '') return null;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return Math.round(n);
}
function americanToDecimal(american) {
  if (american == null || Number.isNaN(american)) return null;
  return american > 0 ? american / 100 + 1 : 100 / Math.abs(american) + 1;
}
function decimalToAmerican(decimal) {
  if (decimal == null || Number.isNaN(decimal) || decimal <= 1) return null;
  return decimal >= 2 ? Math.round((decimal - 1) * 100) : Math.round(-100 / (decimal - 1));
}
function formatAmericanDisplay(american) {
  if (american == null || Number.isNaN(american)) return '';
  const sign = american > 0 ? '+' : '';
  return `${sign}${american}`;
}

/**
 * Build a single-line summary of game-level odds from API-enriched game object.
 * @param {object} game
 * @returns {string | null}
 */
function buildGameOddsSummaryText(game) {
  if (!game) return null;
  const chunks = [];
  const awayMl = game.moneyline_away_american;
  const homeMl = game.moneyline_home_american;
  if (awayMl != null && homeMl != null) {
    chunks.push(`ML ${formatAmericanDisplay(awayMl)} / ${formatAmericanDisplay(homeMl)}`);
  } else if (awayMl != null) {
    chunks.push(`Away ML ${formatAmericanDisplay(awayMl)}`);
  } else if (homeMl != null) {
    chunks.push(`Home ML ${formatAmericanDisplay(homeMl)}`);
  }
  const sap = game.spread_away_point;
  const saa = game.spread_away_american;
  const sapN = sap != null ? Number(sap) : NaN;
  if (Number.isFinite(sapN) && saa != null) {
    const sign = sapN > 0 ? '+' : '';
    chunks.push(`Spread ${sign}${sapN} (${formatAmericanDisplay(saa)})`);
  } else {
    const shp = game.spread_home_point;
    const sha = game.spread_home_american;
    const shpN = shp != null ? Number(shp) : NaN;
    if (Number.isFinite(shpN) && sha != null) {
      const sign = shpN > 0 ? '+' : '';
      chunks.push(`Spread ${sign}${shpN} (${formatAmericanDisplay(sha)})`);
    }
  }
  const tl = game.total_line;
  const tlN = tl != null ? Number(tl) : NaN;
  const to = game.total_over_american;
  const tu = game.total_under_american;
  if (Number.isFinite(tlN)) {
    if (to != null && tu != null) {
      chunks.push(`O/U ${tlN} (O ${formatAmericanDisplay(to)} / U ${formatAmericanDisplay(tu)})`);
    } else if (to != null) {
      chunks.push(`O/U ${tlN} O ${formatAmericanDisplay(to)}`);
    } else if (tu != null) {
      chunks.push(`O/U ${tlN} U ${formatAmericanDisplay(tu)}`);
    } else {
      chunks.push(`O/U ${tlN}`);
    }
  }
  return chunks.length ? chunks.join(' · ') : null;
}

const FaChevronDownStyled = styled(FaChevronDown)`
  color: ${props => props.theme.mutedText};
  font-size: 0.9em;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  @media (max-width: 560px) {
    padding: 1rem 0.75rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.primaryText};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${props => props.theme.secondaryText};
  font-size: 1rem;
  line-height: 1.45;
  font-weight: 400;
  @media (max-width: 560px) {
    font-size: 0.875rem;
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
`;

const ExpandAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.4rem;
  color: ${props => props.theme.secondaryText};
  cursor: pointer;
  margin-left: 0.5rem;
  transition: border-color 0.2s, color 0.2s;
  &:hover, &:focus {
    border-color: ${props => props.theme.primary};
    color: ${props => props.theme.primaryText};
    outline: none;
  }
  @media (max-width: 480px) {
    margin-left: 0;
    margin-top: 0.5rem;
    width: 100%;
    justify-content: center;
  }
`;

const DateNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover, &:focus {
    border: 1.5px solid ${props => props.theme.primaryHover};
    outline: none;
  }
`;

const DateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 1.75rem;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.95rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  &:hover, &:focus {
    border: 1.5px solid ${props => props.theme.primaryHover};
    outline: none;
  }
`;

const CalendarIcon = styled(FaCalendarAlt)`
  color: ${props => props.theme.primary};
  font-size: 1.1em;
`;

const ChevronIcon = styled(FaChevronDown)`
  color: ${props => props.theme.mutedText};
  font-size: 0.95em;
`;

const DateButtonInput = React.forwardRef(({ value, onClick }, ref) => {
  const { theme } = useTheme();
  return (
    <DateButton type="button" onClick={onClick} ref={ref} theme={theme}>
      <CalendarIcon theme={theme} />
      {value}
      <ChevronIcon theme={theme} />
    </DateButton>
  );
});

const GameCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  box-shadow: ${props => props.theme.shadow};
  border-left: 4px solid #4299e1;
  overflow: hidden;
`;

const GameCardHeader = styled.button`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: ${props => props.theme.cardBg};
  border: none;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
  transition: background-color 0.2s ease;
  &:hover {
    background: ${props => props.theme.tertiaryBg || 'rgba(255,255,255,0.05)'};
  }
  &:focus {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: 2px;
  }
  @media (max-width: 560px) {
    padding: 0.85rem 1rem;
    gap: 0.5rem;
  }
`;

/** Wraps time, badges, pick count, chevron so they stay together and wrap below title on mobile */
const GameCardHeaderMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const GameTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  /* On narrow screens take full first row so meta (time, picks, chevron) wraps to second line */
  @media (max-width: 560px) {
    flex: 1 1 100%;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.35;
  }
`;

/** Full-width row under matchup: moneyline / spread / total when consolidated odds exist */
const GameOddsLine = styled.div`
  flex: 1 0 100%;
  width: 100%;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${props => props.theme.secondaryText};
  padding-top: 0.15rem;
  @media (max-width: 560px) {
    font-size: 0.72rem;
  }
`;

const AtSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
  color: ${props => props.theme.secondaryText};
  font-weight: 500;
`;

const GameTimeLabel = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
  flex-shrink: 0;
`;

const PickCountBadge = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  background: ${props => props.$empty ? (props.theme.secondaryBg || 'rgba(255,255,255,0.1)') : props.theme.primary};
  color: ${props => props.$empty ? props.theme.secondaryText : props.theme.inverseText};
  flex-shrink: 0;
`;

const ProjectedBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  background: ${props => props.theme.secondaryBg || 'rgba(255,255,255,0.08)'};
  color: ${props => props.theme.secondaryText};
  flex-shrink: 0;
  border: 1px solid ${props => props.theme.border};
`;

const GameCardBody = styled.div`
  padding: 1.25rem 1.25rem 1.25rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const GameBlurb = styled.p`
  color: ${props => props.theme.secondaryText};
  font-size: 0.95rem;
  margin: 0.75rem 0 1rem;
`;

const TbSummary = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 0.75rem;
  & strong { color: ${props => props.theme.primaryText}; }
`;

/** Final score line (winner omitted; implied by score) */
const FinalScoreBlock = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  & strong { color: ${props => props.theme.primaryText}; }
`;

/** Probable starters: away @ home, from pick enrichment (same pitcher fields as model cards) */
const PitcherMatchupLine = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.35rem;
  line-height: 1.35;
`;

const PitcherMatchupName = styled.span`
  color: ${props => props.theme.primaryText};
  font-weight: 500;
`;

const PitcherMatchupVs = styled.span`
  color: ${props => props.theme.mutedText};
  font-weight: 500;
`;

/** Card wrapper for cumulative TB: subtle background, border, and spacing */
const TbSummaryCard = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 0.4rem;
  background: ${props => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border-left: 3px solid ${props => props.$borderColor ?? props.theme.border};
`;

/** Cell for logo + optional result emoji + track under the TB bar */
const TbLogoCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$align === 'right' ? 'flex-end' : 'flex-start')};
  gap: 0.35rem;
  flex-wrap: nowrap;
  /* Hide the text part of TeamWithLogo so we only show the icon */
  & > span > span {
    display: none;
  }
`;

/** Row of team logos underneath the TB bar (mobile-friendly: logo-only, no text). */
const TbLogosRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.3rem;
  margin-bottom: 0.15rem;
`;

const TbSummaryLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${props => props.theme.mutedText};
  margin-bottom: 0.4rem;
`;

/** Bar track: center = 0, left end = 5 (Away favored), right end = 5 (Home favored). Fill extends from center toward favored side. */
const TbBarTrack = styled.div`
  display: flex;
  align-items: stretch;
  height: 10px;
  border-radius: 5px;
  background: ${props => props.theme.border || '#4a5568'};
  overflow: visible;
  margin-bottom: 0.5rem;
  min-width: 140px;
`;

const TbBarHalf = styled.div`
  flex: 1;
  display: flex;
  justify-content: ${props => (props.$side === 'left' ? 'flex-end' : 'flex-start')};
  align-items: stretch;
  min-width: 0;
`;

const TbBarFill = styled.div`
  height: 100%;
  width: ${props => props.$widthPct}%;
  min-width: 0;
  background: ${props => props.$color};
  border-radius: ${props => (props.$side === 'left' ? '5px 0 0 5px' : '0 5px 5px 0')};
  transition: width 0.25s ease;
`;

const TbBarCenter = styled.div`
  width: 2px;
  flex-shrink: 0;
  background: ${props => props.theme.primaryText};
  opacity: 0.8;
  border-radius: 1px;
`;

const TbSummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryText};
  & strong { color: ${props => props.theme.primaryText}; }
`;

const TbTeamCell = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: ${props => (props.$align === 'right' ? 'flex-end' : 'flex-start')};
  gap: 0.35rem;
  text-align: ${props => (props.$align === 'right' ? 'right' : 'left')};
`;

const TbTeamLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

/** Delta badge for cumulative TB: color-coded by magnitude (e.g. green >= 3, yellow 0–<3) */
const TbDeltaBadge = styled.span`
  display: inline-block;
  margin-left: 0.25rem;
  padding: 0.15rem 0.45rem;
  border-radius: 0.25rem;
  font-weight: 600;
  font-size: 0.8rem;
  background-color: ${props => props.$color};
  color: ${props => props.$textColor ?? '#1a202c'};
`;

/** Small "Favored" pill next to the delta for clarity */
const TbFavoredPill = styled.span`
  display: inline-block;
  margin-left: 0.25rem;
  padding: 0.1rem 0.35rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${props => props.theme.tertiaryBg || 'rgba(255,255,255,0.08)'};
  color: ${props => props.theme.mutedText};
`;

const ProjectedWinnerBlock = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: ${props => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border-radius: 0.4rem;
  border-left: 3px solid ${props => props.theme.primary};
`;
const ProjectedWinnerLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${props => props.theme.primary};
  display: block;
  margin-bottom: 0.25rem;
`;
const ProjectedWinnerTeam = styled.span`
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  font-size: 1rem;
`;
const ProjectedWinnerBlurb = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
  line-height: 1.4;
`;

const SideSection = styled.div`
  margin-top: 0.75rem;
`;

/** On desktop: two columns so Home picks align under left of TB line, Away under right. On mobile: stacked. */
const PicksColumns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
  @media (min-width: 769px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const PicksColumn = styled.div`
  flex: 1;
  min-width: 0;
  @media (min-width: 769px) {
    max-width: 50%;
  }
`;

const SideLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  display: block;
  margin-bottom: 0.25rem;
`;

/** Small indicator when lineup has been posted for a team */
const LineupPostedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0.35rem;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${props => props.theme.tertiaryBg || 'rgba(255,255,255,0.08)'};
  color: ${props => props.theme.mutedText || '#a0aec0'};
  border: 1px solid ${props => props.theme.border || '#4a5568'};
`;

/** Lighter checkmark for lineup posted (distinct from result ✅) */
const LineupCheckIcon = styled(FaCheck)`
  color: ${props => props.theme.secondaryText || '#b8bcc8'};
  flex-shrink: 0;
`;

/** Small indicator when lineup has not yet been posted for a team */
const LineupNotPostedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0.35rem;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${props => props.theme.tertiaryBg || 'rgba(255,255,255,0.08)'};
  color: ${props => props.theme.mutedText || '#a0aec0'};
  border: 1px solid ${props => props.theme.border || '#4a5568'};
`;

const NotAvailable = styled.div`
  text-align: center;
  padding: 3rem;
  background: ${props => props.theme.secondaryBg};
  border-radius: 0.5rem;
  color: ${props => props.theme.secondaryText};
`;

const TrackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0.3rem 0.45rem;
  min-width: 2rem;
  margin-left: ${(props) => (props.$byLogo ? '0' : 'auto')};
  flex-shrink: 0;
  font-size: 0.8rem;
  background: ${props => props.theme.primary};
  color: ${props => props.theme.primaryContrast || '#fff'};
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${props => props.theme.border || props.theme.secondaryBg};
    color: ${props => props.theme.secondaryText};
  }
`;

const PickItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0;
  padding-left: 0.5rem;
  border-left: 2px solid ${props => props.theme.border};
  margin-left: 0.25rem;
  margin-bottom: 0.15rem;
  color: ${props => props.theme.primaryText};
  font-size: 0.875rem;
  width: 100%;
  & > span { min-width: 0; }
`;

/** Player + copy link to model page (?date=&player=&expand=1), like Picks of the Day. */
const PickToModelLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
  border-radius: 0.25rem;
  padding: 0.1rem 0.2rem;
  margin: -0.1rem -0.2rem;
  transition: background-color 0.15s ease;
  &:hover {
    background: ${(props) => props.theme.secondaryBg || 'rgba(0, 0, 0, 0.06)'};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.primary};
    outline-offset: 2px;
  }
  & > span {
    min-width: 0;
  }
`;

/** Navigates to Team Picks; use inside game card header with stopPropagation so expand/collapse still works. */
const TeamPicksNavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  border-radius: 0.25rem;
  &:hover {
    opacity: 0.92;
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.primary};
    outline-offset: 2px;
  }
`;

const SideTeamLink = styled(Link)`
  color: ${(props) => props.theme.primaryText};
  font-weight: 600;
  text-decoration: none;
  border-radius: 0.2rem;
  &:hover {
    text-decoration: underline;
    color: ${(props) => props.theme.primary};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.primary};
    outline-offset: 2px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
`;

const ModalBox = styled.div`
  background: ${props => props.theme.cardBg || props.theme.primaryBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.5rem;
  padding: 1.25rem;
  max-width: 400px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.75rem;
  color: ${props => props.theme.primaryText};
  font-size: 1.1rem;
`;

const ModalSummary = styled.p`
  margin: 0 0 1rem;
  color: ${props => props.theme.secondaryText};
  font-size: 0.9rem;
`;

const ModalInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
  color: ${props => props.theme.secondaryText};
  font-size: 0.875rem;
`;

const OptionalIcon = styled(FaInfoCircle).attrs({ title: 'Optional' })`
  opacity: 0.7;
  flex-shrink: 0;
`;

const ModalInputWrap = styled.div`
  display: flex;
  align-items: stretch;
  margin-bottom: 1rem;
  border-radius: 0.35rem;
  overflow: hidden;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.inputBg};
`;

const ModalInputPrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  background: ${props => props.theme.inputBg};
  color: ${props => props.theme.secondaryText};
  font-size: 1rem;
  border-right: 1px solid ${props => props.theme.border};
`;

const ModalInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
  font-size: 1rem;
  box-sizing: border-box;
  &:focus { outline: none; }
`;

const OddsFormatToggle = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
`;
const OddsFormatBtn = styled.button`
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 0.25rem;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.$active ? props.theme.primary : 'transparent'};
  color: ${props => props.$active ? (props.theme.primaryContrast || '#fff') : props.theme.primaryText};
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const ModalButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.35rem;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.$primary ? props.theme.primary : 'transparent'};
  color: ${props => props.$primary ? (props.theme.primaryContrast || '#fff') : props.theme.primaryText};
  &:hover { opacity: 0.9; }
`;

const GamePicksPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  usePageTitle('Game Picks – Curated MLB Player Props & Parlays');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [expandedGames, setExpandedGames] = useState(() => new Set());
  const [trackModal, setTrackModal] = useState(null);
  const [trackStake, setTrackStake] = useState('');
  const [trackOddsInput, setTrackOddsInput] = useState('');
  const [trackOddsFormat, setTrackOddsFormat] = useState('american');
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState(null);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const { data, isLoading: loading } = useGamePicks(dateStr);

  // Expand all games by default when the date or game list loads
  const expandedInitKeyRef = React.useRef('');
  useEffect(() => {
    if (loading || !data?.games?.length) return;
    const gameIds = [...data.games].map((g) => String(g.game_id)).sort().join(',');
    const initKey = `${dateStr}:${gameIds}`;
    if (expandedInitKeyRef.current === initKey) return;
    expandedInitKeyRef.current = initKey;
    setExpandedGames(new Set(data.games.map((g) => g.game_id)));
  }, [dateStr, loading, data?.games]);

  const toggleGame = (gameId) => {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      return next;
    });
  };

  const openTrackModal = (pick, game, dateStr) => {
    setTrackModal({ pick, game, dateStr });
    setTrackStake('');
    const raw = (pick.odds_display != null && String(pick.odds_display).trim() !== '')
      ? String(pick.odds_display).replace(/\s*\([^)]*\)\s*$/, '').trim()
      : '';
    setTrackOddsInput(raw);
    setTrackOddsFormat('american');
    setTrackError(null);
  };

  const closeTrackModal = () => {
    setTrackModal(null);
    setTrackStake('');
    setTrackOddsInput('');
    setTrackError(null);
  };

  const submitTrackPick = async () => {
    if (!trackModal) return;
    const { pick, game, dateStr } = trackModal;
    setTrackLoading(true);
    setTrackError(null);
    try {
      const stakeDollars = trackStake.trim() === '' ? undefined : parseFloat(trackStake);
      if (stakeDollars !== undefined && (isNaN(stakeDollars) || stakeDollars < 0)) {
        setTrackError('Enter a valid dollar amount or leave empty.');
        setTrackLoading(false);
        return;
      }
      let oddsAmerican = null;
      let oddsDisplayStr = null;
      const oddsStr = trackOddsInput.trim();
      if (oddsStr !== '') {
        if (trackOddsFormat === 'american') {
          oddsAmerican = parseAmericanOdds(oddsStr);
          if (oddsAmerican == null) {
            setTrackError('Enter valid American odds (e.g. +150 or -200).');
            setTrackLoading(false);
            return;
          }
          oddsDisplayStr = formatAmericanDisplay(oddsAmerican);
        } else {
          const dec = parseFloat(oddsStr);
          if (Number.isNaN(dec) || dec <= 1) {
            setTrackError('Enter valid decimal odds (e.g. 1.92, must be > 1).');
            setTrackLoading(false);
            return;
          }
          oddsAmerican = decimalToAmerican(dec);
          oddsDisplayStr = formatAmericanDisplay(oddsAmerican);
        }
      }
      const body = {
        game_date: dateStr,
        game_id: game.game_id,
        player_id: pick.player_id,
        player_name: pick.player_name,
        pick_type: pick.pick_type,
        prediction_value: pick.prediction_value,
        team_id: pick.team_id ?? undefined,
        team_name: pick.team_name ?? undefined,
        stake_dollars: stakeDollars,
      };
      if (oddsAmerican != null) body.odds_american = oddsAmerican;
      if (oddsDisplayStr != null) body.odds_display = oddsDisplayStr;
      await createWatchedPick(body);
      closeTrackModal();
    } catch (err) {
      setTrackError(err.response?.data?.error || 'Failed to add pick. Try again.');
    } finally {
      setTrackLoading(false);
    }
  };

  const hasGames = data?.games?.length > 0;

  // Same format as model pages (e.g. "7:05 PM")
  const formatGameTime = (isoString) => {
    if (!isoString) return 'TBD';
    try {
      const date = new Date(isoString);
      return format(date, 'h:mm a');
    } catch (e) {
      return 'TBD';
    }
  };

  // Sort chronologically by game time (earliest first); games without a parseable time sort last
  const sortedGames = hasGames
    ? [...data.games].sort((a, b) => {
        const ta = a.game_time ? new Date(a.game_time).getTime() : NaN;
        const tb = b.game_time ? new Date(b.game_time).getTime() : NaN;
        const aOk = Number.isFinite(ta);
        const bOk = Number.isFinite(tb);
        if (!aOk && !bOk) return 0;
        if (!aOk) return 1;
        if (!bOk) return -1;
        return ta - tb;
      })
    : [];

  return (
    <Container>
      <PageMeta description="Curated MLB game picks built from Slick Picks' player prop models, showing the best Total Bases, Hits, and Home Run edges for each matchup." />
      <Header>
        <Title theme={theme}>Game Picks</Title>
        <Subtitle theme={theme}>AI-powered favorable picks per game for both teams</Subtitle>
      </Header>
      <DateRow>
        <DateNavButton
          type="button"
          theme={theme}
          onClick={() => setSelectedDate((d) => addDays(d, -1))}
          aria-label="Previous day"
        >
          <FaChevronLeft aria-hidden />
        </DateNavButton>
        <DatePicker
          id="date-picker-game-picks"
          selected={selectedDate}
          onChange={(date) => { setSelectedDate(date); setIsDatePickerOpen(false); }}
          dateFormat="yyyy-MM-dd"
          customInput={<DateButtonInput />}
          popperPlacement="bottom-start"
          open={isDatePickerOpen}
          onClickOutside={() => setIsDatePickerOpen(false)}
          onInputClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
        />
        <DateNavButton
          type="button"
          theme={theme}
          onClick={() => setSelectedDate((d) => addDays(d, 1))}
          aria-label="Next day"
        >
          <FaChevronRight aria-hidden />
        </DateNavButton>
        {hasGames && sortedGames.length > 0 && (
          <ExpandAllBtn
            type="button"
            theme={theme}
            onClick={() => {
              const allIds = new Set(sortedGames.map((g) => g.game_id));
              const allExpanded = allIds.size > 0 && expandedGames.size === allIds.size;
              setExpandedGames(allExpanded ? new Set() : allIds);
            }}
            aria-label={expandedGames.size === sortedGames.length ? 'Collapse all game picks' : 'Expand all game picks'}
          >
            {expandedGames.size === sortedGames.length && sortedGames.length > 0 ? (
              <>
                <FaCompressAlt aria-hidden /> Collapse all
              </>
            ) : (
              <>
                <FaExpandAlt aria-hidden /> Expand all
              </>
            )}
          </ExpandAllBtn>
        )}
      </DateRow>
      {loading && <p style={{ textAlign: 'center', color: theme.secondaryText }}>Loading...</p>}
      {!loading && !hasGames && (
        <NotAvailable theme={theme}>
          <p>Game Picks are not available for this date.</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>They are generated periodically; check back later.</p>
        </NotAvailable>
      )}
      {!loading && hasGames && sortedGames.map((game) => {
        const pickCount = (game.favorable_picks?.away?.length ?? 0) + (game.favorable_picks?.home?.length ?? 0);
        const isExpanded = expandedGames.has(game.game_id);
        const hasPicks = pickCount > 0;
        const gameOddsText = buildGameOddsSummaryText(game);
        return (
          <GameCard key={game.game_id} theme={theme}>
            <GameCardHeader
              type="button"
              theme={theme}
              onClick={() => toggleGame(game.game_id)}
              aria-expanded={isExpanded}
              aria-controls={`game-picks-${game.game_id}`}
              id={`game-header-${game.game_id}`}
            >
              <GameTitle theme={theme}>
                {game.away_team_id != null ? (
                  <TeamPicksNavLink
                    to={buildTeamPicksPath({ teamId: game.away_team_id, date: dateStr, teamName: game.away_team })}
                    onClick={(e) => e.stopPropagation()}
                    title={`Team Picks — ${game.away_team ?? 'Away'}`}
                  >
                    <TeamWithLogo teamId={game.away_team_id} teamName={game.away_team} size={22} />
                  </TeamPicksNavLink>
                ) : (
                  <TeamWithLogo teamId={game.away_team_id} teamName={game.away_team} size={22} />
                )}
                <AtSeparator theme={theme}>@</AtSeparator>
                {game.home_team_id != null ? (
                  <TeamPicksNavLink
                    to={buildTeamPicksPath({ teamId: game.home_team_id, date: dateStr, teamName: game.home_team })}
                    onClick={(e) => e.stopPropagation()}
                    title={`Team Picks — ${game.home_team ?? 'Home'}`}
                  >
                    <TeamWithLogo teamId={game.home_team_id} teamName={game.home_team} size={22} />
                  </TeamPicksNavLink>
                ) : (
                  <TeamWithLogo teamId={game.home_team_id} teamName={game.home_team} size={22} />
                )}
              </GameTitle>
              <GameCardHeaderMeta>
                <GameTimeLabel theme={theme}>{formatGameTime(game.game_time)}</GameTimeLabel>
                {game.game_status && (
                  <ProjectedBadge theme={theme} title="Game status">
                    {game.game_status}
                  </ProjectedBadge>
                )}
                {game.projected_winner && (
                  <ProjectedBadge theme={theme} title="Projected winner (expand for details)">
                    {game.projected_winner}
                  </ProjectedBadge>
                )}
                <PickCountBadge theme={theme} $empty={!hasPicks}>
                  {pickCount === 0 ? '0 picks' : `${pickCount} pick${pickCount !== 1 ? 's' : ''}`}
                </PickCountBadge>
                <FaChevronDownStyled theme={theme} $expanded={isExpanded} aria-hidden />
              </GameCardHeaderMeta>
              {gameOddsText && (
                <GameOddsLine theme={theme}>
                  <span style={{ fontWeight: 600, color: theme?.mutedText }}>Odds</span>
                  {' · '}
                  {gameOddsText}
                </GameOddsLine>
              )}
            </GameCardHeader>
            {isExpanded && (
              <GameCardBody id={`game-picks-${game.game_id}`} theme={theme} role="region" aria-labelledby={`game-header-${game.game_id}`}>
                {game.game_status && (
                  <TbSummary theme={theme}>
                    <strong>Game status:</strong>{' '}
                    <StatusWithGamedayLink status={game.game_status} gameId={game.game_id} />
                    {isGameFinal(game.game_status) && ' — game complete'}
                  </TbSummary>
                )}
                {(() => {
                  const { away: awayStarter, home: homeStarter } = resolveGameStarters(game);
                  const awayLabel = pitcherStarterLabel(awayStarter);
                  const homeLabel = pitcherStarterLabel(homeStarter);
                  if (!awayLabel && !homeLabel) return null;
                  return (
                    <PitcherMatchupLine theme={theme}>
                      <PitcherMatchupName theme={theme}>{awayLabel || '—'}</PitcherMatchupName>
                      <PitcherMatchupVs theme={theme}>vs.</PitcherMatchupVs>
                      <PitcherMatchupName theme={theme}>{homeLabel || '—'}</PitcherMatchupName>
                    </PitcherMatchupLine>
                  );
                })()}
                {isGameFinal(game.game_status) && game.away_score != null && game.home_score != null && (
                  <FinalScoreBlock theme={theme}>
                    <div>
                      <strong>Final:</strong> {game.away_team || 'Away'} {game.away_score},{' '}
                      {game.home_team || 'Home'} {game.home_score}
                    </div>
                  </FinalScoreBlock>
                )}
                {(game.away_total_tb != null || game.home_total_tb != null) && (() => {
                  const awayTb = Math.max(0, Number(game.away_total_tb ?? 0));
                  const homeTb = Math.max(0, Number(game.home_total_tb ?? 0));
                  // Do not show TB card until both lineups are posted
                  if (!game.home_lineup_posted || !game.away_lineup_posted) {
                    return null;
                  }
                  const delta = Math.abs(awayTb - homeTb);
                  const favored = awayTb >= homeTb ? 'away' : 'home';
                  const favoredTeamName = favored === 'away' ? game.away_team : game.home_team;
                  const gameFinal = isGameFinal(game.game_status ?? game.gameStatus);
                  const hasWinner = Boolean(game.winner);
                  const favoredWon =
                    gameFinal &&
                    hasWinner &&
                    String(game.winner).trim() === String(favoredTeamName || '').trim();
                  const favoredLostAway =
                    gameFinal &&
                    hasWinner &&
                    favored === 'away' &&
                    String(game.winner).trim() !== String(game.away_team || '').trim();
                  const favoredLostHome =
                    gameFinal &&
                    hasWinner &&
                    favored === 'home' &&
                    String(game.winner).trim() !== String(game.home_team || '').trim();
                  const deltaColor =
                    delta >= 3
                      ? theme.success
                      : delta >= 1
                        ? '#ecc94b'
                        : theme.warning;
                  const deltaTextColor = '#1a202c';
                  const showDelta = delta > 0;
                  const scaleMax = 5;
                  const fillPct = showDelta ? (Math.min(delta, scaleMax) / scaleMax) * 100 : 0;
                  return (
                    <TbSummaryCard theme={theme} $borderColor={showDelta ? deltaColor : undefined}>
                      <TbSummaryLabel theme={theme}>
                        <FaChartBar size={12} aria-hidden />
                        Cumulative projected TB
                      </TbSummaryLabel>
                      <TbBarTrack theme={theme}>
                        {/* Left = Away (first in matchup order), Right = Home */}
                        <TbBarHalf $side="left">
                          {favored === 'away' && showDelta && (
                            <TbBarFill $side="left" $widthPct={fillPct} $color={deltaColor} />
                          )}
                        </TbBarHalf>
                        <TbBarCenter theme={theme} />
                        <TbBarHalf $side="right">
                          {favored === 'home' && showDelta && (
                            <TbBarFill $side="right" $widthPct={fillPct} $color={deltaColor} />
                          )}
                        </TbBarHalf>
                      </TbBarTrack>
                      <TbLogosRow>
                        <TbLogoCell $align="left">
                          <TeamWithLogo teamId={game.away_team_id} teamName={game.away_team} size={18} />
                          {favored === 'away' && favoredWon && (
                            <span role="img" aria-label="Favored team won" title="Favored team (most projected TB) won">✅</span>
                          )}
                          {favoredLostAway && (
                            <span role="img" aria-label="Favored team lost" title="Favored team (most projected TB) did not win">❌</span>
                          )}
                          {isGameNotStarted(game.game_status ?? game.gameStatus) && (
                            <TrackButton
                              type="button"
                              theme={theme}
                              $byLogo
                              disabled={!user}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!user) return;
                                const ml = game.moneyline_away_american;
                                const raw = ml != null ? (ml > 0 ? `+${ml}` : String(ml)) : '';
                                openTrackModal(
                                  {
                                    pick_type: 'moneyline',
                                    team_id: game.away_team_id,
                                    team_name: game.away_team,
                                    player_id: 0,
                                    player_name: game.away_team,
                                    odds_display: raw || undefined,
                                    prediction_value: null,
                                  },
                                  game,
                                  dateStr
                                );
                              }}
                              title={user ? 'Track moneyline for this team' : 'Log in to track'}
                              aria-label={user ? 'Track moneyline for this team' : 'Log in to track'}
                            >
                              <FaEye aria-hidden />
                            </TrackButton>
                          )}
                        </TbLogoCell>
                        <TbLogoCell $align="right">
                          <TeamWithLogo teamId={game.home_team_id} teamName={game.home_team} size={18} />
                          {favored === 'home' && favoredWon && (
                            <span role="img" aria-label="Favored team won" title="Favored team (most projected TB) won">✅</span>
                          )}
                          {favoredLostHome && (
                            <span role="img" aria-label="Favored team lost" title="Favored team (most projected TB) did not win">❌</span>
                          )}
                          {isGameNotStarted(game.game_status ?? game.gameStatus) && (
                            <TrackButton
                              type="button"
                              theme={theme}
                              $byLogo
                              disabled={!user}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!user) return;
                                const ml = game.moneyline_home_american;
                                const raw = ml != null ? (ml > 0 ? `+${ml}` : String(ml)) : '';
                                openTrackModal(
                                  {
                                    pick_type: 'moneyline',
                                    team_id: game.home_team_id,
                                    team_name: game.home_team,
                                    player_id: 0,
                                    player_name: game.home_team,
                                    odds_display: raw || undefined,
                                    prediction_value: null,
                                  },
                                  game,
                                  dateStr
                                );
                              }}
                              title={user ? 'Track moneyline for this team' : 'Log in to track'}
                              aria-label={user ? 'Track moneyline for this team' : 'Log in to track'}
                            >
                              <FaEye aria-hidden />
                            </TrackButton>
                          )}
                        </TbLogoCell>
                      </TbLogosRow>
                      <TbSummaryRow theme={theme}>
                        {/* Away team (first in matchup): left */}
                        <TbTeamCell $align="left">
                          <TbTeamLine>
                            {favored === 'away' ? (
                              <>
                                <strong>{awayTb.toFixed(1)}</strong>
                                {showDelta && (
                                  <>
                                    <TbDeltaBadge $color={deltaColor} $textColor={deltaTextColor}>+{delta.toFixed(1)}</TbDeltaBadge>
                                    <TbFavoredPill theme={theme}>Favored</TbFavoredPill>
                                  </>
                                )}
                              </>
                            ) : (
                              <span>{awayTb.toFixed(1)}</span>
                            )}
                          </TbTeamLine>
                        </TbTeamCell>
                        {/* Home team (second in matchup): right */}
                        <TbTeamCell $align="right">
                          <TbTeamLine>
                            {favored === 'home' ? (
                              <>
                                {showDelta && (
                                  <>
                                    <TbDeltaBadge $color={deltaColor} $textColor={deltaTextColor}>+{delta.toFixed(1)}</TbDeltaBadge>
                                    <TbFavoredPill theme={theme}>Favored</TbFavoredPill>
                                  </>
                                )}
                                <strong>{homeTb.toFixed(1)}</strong>
                              </>
                            ) : (
                              <span>{homeTb.toFixed(1)}</span>
                            )}
                          </TbTeamLine>
                        </TbTeamCell>
                      </TbSummaryRow>
                    </TbSummaryCard>
                  );
                })()}
                {(game.projected_winner || game.projected_winner_blurb) && (
                  <ProjectedWinnerBlock theme={theme}>
                    <ProjectedWinnerLabel theme={theme}>Projected winner</ProjectedWinnerLabel>
                    {game.projected_winner && <ProjectedWinnerTeam theme={theme}>{game.projected_winner}</ProjectedWinnerTeam>}
                    {game.projected_winner_blurb && (
                      <ProjectedWinnerBlurb theme={theme}>{game.projected_winner_blurb}</ProjectedWinnerBlurb>
                    )}
                  </ProjectedWinnerBlock>
                )}
                {game.game_summary_blurb && <GameBlurb theme={theme}>{game.game_summary_blurb}</GameBlurb>}
                <PicksColumns>
                    {/* First in matchup order: Away (e.g. Yankees) */}
                    <PicksColumn>
                      <SideSection>
                        <SideLabel theme={theme}>
                          Away (
                          {game.away_team_id != null ? (
                            <SideTeamLink
                              theme={theme}
                              to={buildTeamPicksPath({ teamId: game.away_team_id, date: dateStr, teamName: game.away_team })}
                            >
                              {game.away_team}
                            </SideTeamLink>
                          ) : (
                            game.away_team
                          )}
                          ):
                          {game.away_lineup_posted ? (
                            <LineupPostedBadge theme={theme} title="Lineup posted">
                              <FaRegFileAlt size={10} />
                              <LineupCheckIcon theme={theme} size={10} aria-label="Lineup posted" />
                            </LineupPostedBadge>
                          ) : (
                            <LineupNotPostedBadge theme={theme} title="Lineup not yet posted">
                              Lineup not posted
                            </LineupNotPostedBadge>
                          )}
                        </SideLabel>
                        {game.favorable_picks?.away?.length > 0 ? (
                          sortPicksByModelThenValue(game.favorable_picks.away).map((p, i) => {
                            const resultStr = formatPickResult(p, { assumeFinal: true });
                            const modelHref = buildModelPageDeepLink({
                              pickType: p.pick_type,
                              playerName: p.player_name,
                              date: dateStr,
                            });
                            return (
                              <PickItemRow key={`${game.game_id}-away-${p.player_id}-${p.pick_type}-${i}`} theme={theme}>
                                <PickToModelLink
                                  theme={theme}
                                  to={modelHref}
                                  title={`View ${p.player_name || 'player'} on ${formatPickTypeLabel(p.pick_type)} (expanded card)`}
                                >
                                  <PlayerWithAvatar playerId={p.player_id} playerName={p.player_name} size={22} />
                                  <span>
                                    {' — '}{formatPickTypeLabel(p.pick_type)} {p.prediction_value != null && `(${formatPickValue(p.pick_type, p.prediction_value)})`}
                                    {p.odds_display && <span style={{ marginLeft: '0.35rem', color: theme.secondaryText }}>· {p.odds_display}</span>}
                                    {p.edge_display != null && <span style={{ marginLeft: '0.25rem', color: theme.secondaryText }}>· {Number(p.edge_display * 100).toFixed(1)}% edge</span>}
                                    {p.blurb && ` — ${p.blurb}`}
                                    {isGameFinal(game.game_status ?? game.gameStatus) && resultStr && (
                                      <span style={{ marginLeft: '0.35rem', fontWeight: 500 }}>· Result: {resultStr}</span>
                                    )}
                                  </span>
                                </PickToModelLink>
                                {isGameNotStarted(game.game_status ?? game.gameStatus) && (
                                  <TrackButton type="button" theme={theme} disabled={!user} onClick={(e) => { e.stopPropagation(); if (user) openTrackModal(p, game, dateStr); }} title={user ? 'Track this pick' : 'Log in to track'} aria-label={user ? 'Track this pick' : 'Log in to track'}>
                                    <FaEye aria-hidden />
                                  </TrackButton>
                                )}
                              </PickItemRow>
                            );
                          })
                        ) : (
                          <p style={{ color: theme?.secondaryText, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
                            No favorable picks for this team.
                          </p>
                        )}
                      </SideSection>
                    </PicksColumn>
                    {/* Second in matchup order: Home (e.g. Rays) */}
                    <PicksColumn>
                      <SideSection>
                        <SideLabel theme={theme}>
                          Home (
                          {game.home_team_id != null ? (
                            <SideTeamLink
                              theme={theme}
                              to={buildTeamPicksPath({ teamId: game.home_team_id, date: dateStr, teamName: game.home_team })}
                            >
                              {game.home_team}
                            </SideTeamLink>
                          ) : (
                            game.home_team
                          )}
                          ):
                          {game.home_lineup_posted ? (
                            <LineupPostedBadge theme={theme} title="Lineup posted">
                              <FaRegFileAlt size={10} />
                              <LineupCheckIcon theme={theme} size={10} aria-label="Lineup posted" />
                            </LineupPostedBadge>
                          ) : (
                            <LineupNotPostedBadge theme={theme} title="Lineup not yet posted">
                              Lineup not posted
                            </LineupNotPostedBadge>
                          )}
                        </SideLabel>
                        {game.favorable_picks?.home?.length > 0 ? (
                          sortPicksByModelThenValue(game.favorable_picks.home).map((p, i) => {
                            const resultStr = formatPickResult(p, { assumeFinal: true });
                            const modelHref = buildModelPageDeepLink({
                              pickType: p.pick_type,
                              playerName: p.player_name,
                              date: dateStr,
                            });
                            return (
                              <PickItemRow key={`${game.game_id}-home-${p.player_id}-${p.pick_type}-${i}`} theme={theme}>
                                <PickToModelLink
                                  theme={theme}
                                  to={modelHref}
                                  title={`View ${p.player_name || 'player'} on ${formatPickTypeLabel(p.pick_type)} (expanded card)`}
                                >
                                  <PlayerWithAvatar playerId={p.player_id} playerName={p.player_name} size={22} />
                                  <span>
                                    {' — '}{formatPickTypeLabel(p.pick_type)} {p.prediction_value != null && `(${formatPickValue(p.pick_type, p.prediction_value)})`}
                                    {p.odds_display && <span style={{ marginLeft: '0.35rem', color: theme.secondaryText }}>· {p.odds_display}</span>}
                                    {p.edge_display != null && <span style={{ marginLeft: '0.25rem', color: theme.secondaryText }}>· {Number(p.edge_display * 100).toFixed(1)}% edge</span>}
                                    {p.blurb && ` — ${p.blurb}`}
                                    {isGameFinal(game.game_status ?? game.gameStatus) && resultStr && (
                                      <span style={{ marginLeft: '0.35rem', fontWeight: 500 }}>· Result: {resultStr}</span>
                                    )}
                                  </span>
                                </PickToModelLink>
                                {isGameNotStarted(game.game_status ?? game.gameStatus) && (
                                  <TrackButton type="button" theme={theme} disabled={!user} onClick={(e) => { e.stopPropagation(); if (user) openTrackModal(p, game, dateStr); }} title={user ? 'Track this pick' : 'Log in to track'} aria-label={user ? 'Track this pick' : 'Log in to track'}>
                                    <FaEye aria-hidden />
                                  </TrackButton>
                                )}
                              </PickItemRow>
                            );
                          })
                        ) : (
                          <p style={{ color: theme?.secondaryText, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
                            No favorable picks for this team.
                          </p>
                        )}
                      </SideSection>
                    </PicksColumn>
                  </PicksColumns>
              </GameCardBody>
            )}
          </GameCard>
        );
      })}
      {trackModal && (
        <ModalOverlay onClick={closeTrackModal} role="dialog" aria-labelledby="track-pick-title">
          <ModalBox theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalTitle id="track-pick-title" theme={theme}>Track this pick</ModalTitle>
            <ModalSummary theme={theme}>
              {trackModal.pick.player_name} — {formatPickTypeLabel(trackModal.pick.pick_type)}
              {trackModal.pick.prediction_value != null && ` (${formatPickValue(trackModal.pick.pick_type, trackModal.pick.prediction_value)})`}
              {trackModal.pick.odds_display && ` · Odds ${trackModal.pick.odds_display}`}
            </ModalSummary>
            <ModalInputLabel theme={theme}>
              Simulated wager <OptionalIcon theme={theme} aria-label="Optional" />
            </ModalInputLabel>
            <ModalInputWrap theme={theme}>
              <ModalInputPrefix theme={theme}>$</ModalInputPrefix>
              <ModalInput
                theme={theme}
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 10"
                value={trackStake}
                onChange={(e) => setTrackStake(e.target.value)}
                disabled={trackLoading}
              />
            </ModalInputWrap>
            <ModalInputLabel theme={theme}>
              Odds <OptionalIcon theme={theme} aria-label="Optional" />
            </ModalInputLabel>
            <OddsFormatToggle>
              <OddsFormatBtn
                theme={theme}
                $active={trackOddsFormat === 'american'}
                onClick={() => {
                  if (trackOddsFormat === 'decimal') {
                    const dec = parseFloat(trackOddsInput);
                    if (!Number.isNaN(dec) && dec > 1) {
                      const american = decimalToAmerican(dec);
                      setTrackOddsInput(american != null ? formatAmericanDisplay(american) : trackOddsInput);
                    }
                  }
                  setTrackOddsFormat('american');
                }}
                type="button"
              >
                American
              </OddsFormatBtn>
              <OddsFormatBtn
                theme={theme}
                $active={trackOddsFormat === 'decimal'}
                onClick={() => {
                  if (trackOddsFormat === 'american') {
                    const american = parseAmericanOdds(trackOddsInput);
                    if (american != null) {
                      const dec = americanToDecimal(american);
                      setTrackOddsInput(dec != null ? String(dec) : trackOddsInput);
                    }
                  }
                  setTrackOddsFormat('decimal');
                }}
                type="button"
              >
                Decimal
              </OddsFormatBtn>
            </OddsFormatToggle>
            <ModalInput
              theme={theme}
              type="text"
              inputMode={trackOddsFormat === 'decimal' ? 'decimal' : 'text'}
              placeholder={trackOddsFormat === 'american' ? 'e.g. +150 or -200' : 'e.g. 1.92'}
              value={trackOddsInput}
              onChange={(e) => setTrackOddsInput(e.target.value)}
              disabled={trackLoading}
              style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', width: '100%', boxSizing: 'border-box', border: `1px solid ${theme.border}`, borderRadius: '0.35rem', background: theme.inputBg, color: theme.primaryText, fontSize: '1rem' }}
            />
            {trackError && <p style={{ color: theme.error || '#e53e3e', fontSize: '0.875rem', margin: '0 0 0.5rem' }}>{trackError}</p>}
            <ModalActions>
              <ModalButton theme={theme} onClick={closeTrackModal} disabled={trackLoading}>Cancel</ModalButton>
              <ModalButton theme={theme} $primary onClick={submitTrackPick} disabled={trackLoading}>
                {trackLoading ? 'Adding…' : 'Add to my picks'}
              </ModalButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default GamePicksPage;
